import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { usersApi, leavePoliciesApi } from '../../services/api';
import './AdminUsers.css';

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [policyFilter, setPolicyFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [leavePolicies, setLeavePolicies] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [managers, setManagers] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newMember, setNewMember] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    employee_type: 'full_time',
    designation: '',
    company: '',
    department_id: '',
    region_id: '',
    manager_id: '',
    joining_date: '',
    date_of_birth: '',
    mobile_number: '',
    address: '',
    temporary_address: '',
    emergency_contact_name: '',
    emergency_contact_number: '',
    emergency_contact_relation: '',
    country_code: 'IN',
    is_active: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, deptRes, leavePoliciesRes, regionsRes, countriesRes, managersRes] = await Promise.all([
        usersApi.list({
          search: search || undefined,
          role: roleFilter || undefined,
          department_id: deptFilter || undefined,
          company: companyFilter || undefined,
          leave_policy_id: policyFilter || undefined,
          page,
          limit: 20,
        }),
        usersApi.departments(),
        leavePoliciesApi.list(),
        usersApi.regions(),
        usersApi.countries(),
        usersApi.list({ role: 'manager', page: 1, limit: 200 }),
      ]);
      setUsers(usersRes.data.data);
      setPagination(usersRes.data.pagination);
      setDepartments(deptRes.data.data);
      setLeavePolicies(leavePoliciesRes.data.data || []);
      setRegions(regionsRes.data.data || []);
      setCountries(countriesRes.data.data || []);
      setManagers(managersRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, deptFilter, companyFilter, policyFilter, page]);

  useEffect(() => { load(); }, [load]);

  const roleBadge = (role) => {
    const map = { admin: 'badge-rose', manager: 'badge-amber', employee: 'badge-violet' };
    return <span className={`badge ${map[role] || 'badge-gray'}`}>{role}</span>;
  };

  const typeBadge = (type) => (
    <span className={`badge ${type === 'contractor' ? 'badge-amber' : 'badge-sky'}`}>
      {type === 'contractor' ? 'Contractor' : 'Full Time'}
    </span>
  );

  const visibleUsers = policyFilter
    ? users.filter((u) => String(u.leave_policy_id || u.leavePolicy?.id || '') === String(policyFilter))
    : users;

  const resetNewMember = () => {
    setNewMember({
      email: '',
      first_name: '',
      last_name: '',
      role: 'employee',
      employee_type: 'full_time',
      designation: '',
      company: '',
      department_id: '',
      region_id: '',
      manager_id: '',
      joining_date: '',
      date_of_birth: '',
      mobile_number: '',
      address: '',
      temporary_address: '',
      emergency_contact_name: '',
      emergency_contact_number: '',
      emergency_contact_relation: '',
      country_code: 'IN',
      is_active: true,
    });
  };

  const handleCreateMember = async (e) => {
    e.preventDefault();
    if (!newMember.email.trim() || !newMember.first_name.trim() || !newMember.last_name.trim()) {
      toast.error('Email, first name and last name are required');
      return;
    }

    setCreating(true);
    try {
      await usersApi.create({
        email: newMember.email.trim().toLowerCase(),
        first_name: newMember.first_name.trim(),
        last_name: newMember.last_name.trim(),
        role: newMember.role,
        employee_type: newMember.employee_type,
        designation: newMember.designation || null,
        company: newMember.company || null,
        department_id: newMember.department_id || null,
        region_id: newMember.region_id || null,
        manager_id: newMember.manager_id || null,
        joining_date: newMember.joining_date || null,
        date_of_birth: newMember.date_of_birth || null,
        mobile_number: newMember.mobile_number || null,
        address: newMember.address || null,
        temporary_address: newMember.temporary_address || null,
        emergency_contact_name: newMember.emergency_contact_name || null,
        emergency_contact_number: newMember.emergency_contact_number || null,
        emergency_contact_relation: newMember.emergency_contact_relation || null,
        is_active: newMember.is_active,
      });

      toast.success('Member added successfully');
      setShowAddModal(false);
      resetNewMember();
      setPage(1);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add member');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <div>
          <div className="page-title">User Management</div>
          <div className="page-subtitle">Manage all employees, roles and assignments</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          + Add Member
        </button>
      </div>

      {/* Filters */}
      <div className="card admin-filters animate-in">
        <div className="admin-filters-inner">
          <div className="filter-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="filter-search-input"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select className="form-select" style={{ width: 160 }} value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}>
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="employee">Employee</option>
          </select>
          <select className="form-select" style={{ width: 200 }} value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}>
            <option value="">All Departments</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select
            className="form-select"
            style={{ width: 200 }}
            value={companyFilter}
            onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Companies</option>
            <option value="Aays">Aays</option>
            <option value="Aidetic">Aidetic</option>
          </select>
          <select
            className="form-select"
            style={{ width: 220 }}
            value={policyFilter}
            onChange={(e) => { setPolicyFilter(e.target.value); setPage(1); }}
          >
            <option value="">All Policies</option>
            {leavePolicies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => { setSearch(''); setRoleFilter(''); setDeptFilter(''); setCompanyFilter(''); setPolicyFilter(''); setPage(1); }}>
            Reset
          </button>
        </div>
        {pagination.total !== undefined && (
          <span className="filter-count">{pagination.total} employees</span>
        )}
      </div>

      {/* Table */}
      <div className="card animate-in stagger-1" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 40 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
            ))}
          </div>
        ) : visibleUsers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-title">No users found</div>
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Role</th>
                  <th>Type</th>
                  <th>Department</th>
                  <th>Policy</th>
                  <th>Region</th>
                  <th>Manager</th>
                  <th>Joined</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibleUsers.map((u) => {
                  const initials = `${u.first_name?.[0] || ''}${u.last_name?.[0] || ''}`.toUpperCase();
                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className="avatar avatar-sm" style={{ background: u.role === 'admin' ? '#EF4444' : u.role === 'manager' ? '#F59E0B' : 'var(--brand-violet)' }}>
                            {initials}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{u.first_name} {u.last_name}</div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>{roleBadge(u.role)}</td>
                      <td>{typeBadge(u.employee_type)}</td>
                      <td style={{ fontSize: '0.875rem' }}>{u.department?.name || '—'}</td>
                      <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {u.leavePolicy?.name || 'Unassigned'}
                      </td>
                      <td style={{ fontSize: '0.875rem' }}>{u.region?.name || '—'}</td>
                      <td style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        {u.manager ? `${u.manager.first_name} ${u.manager.last_name}` : '—'}
                      </td>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {u.joining_date ? new Date(u.joining_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td>
                        <span className={`badge ${u.is_active ? 'badge-emerald' : 'badge-rose'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => navigate(`/admin/users/${u.id}`)}
                        >
                          Edit →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
            <span className="pagination-info">Page {page} of {pagination.totalPages}</span>
            <button className="btn btn-secondary btn-sm" disabled={page === pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => !creating && setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="page-title" style={{ fontSize: '1.125rem' }}>Add Member</div>
                <div className="page-subtitle">Create a new employee profile</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAddModal(false)} disabled={creating}>✕</button>
            </div>

            <form onSubmit={handleCreateMember}>
              <div className="modal-body">
                <div className="admin-create-grid">
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      className="form-input"
                      type="email"
                      required
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={newMember.role}
                      onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="employee">Employee</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      className="form-input"
                      required
                      value={newMember.first_name}
                      onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      className="form-input"
                      required
                      value={newMember.last_name}
                      onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Employee Type</label>
                    <select
                      className="form-select"
                      value={newMember.employee_type}
                      onChange={(e) => setNewMember({ ...newMember, employee_type: e.target.value })}
                    >
                      <option value="full_time">Full Time</option>
                      <option value="contractor">Contractor</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Joining Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={newMember.joining_date}
                      onChange={(e) => setNewMember({ ...newMember, joining_date: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Date of Birth</label>
                    <input
                      type="date"
                      className="form-input"
                      value={newMember.date_of_birth}
                      onChange={(e) => setNewMember({ ...newMember, date_of_birth: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Designation</label>
                    <input
                      className="form-input"
                      value={newMember.designation}
                      onChange={(e) => setNewMember({ ...newMember, designation: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Company</label>
                    <select
                      className="form-select"
                      value={newMember.company}
                      onChange={(e) => setNewMember({ ...newMember, company: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      <option value="Aays">Aays</option>
                      <option value="Aidetic">Aidetic</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Mobile Number</label>
                    <input
                      className="form-input"
                      value={newMember.mobile_number}
                      onChange={(e) => setNewMember({ ...newMember, mobile_number: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <select
                      className="form-select"
                      value={newMember.department_id}
                      onChange={(e) => setNewMember({ ...newMember, department_id: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Country</label>
                    <select
                      className="form-select"
                      value={newMember.country_code}
                      onChange={(e) => setNewMember({ ...newMember, country_code: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Region</label>
                    <select
                      className="form-select"
                      value={newMember.region_id}
                      onChange={(e) => setNewMember({ ...newMember, region_id: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      {regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>

                  <div className="form-group admin-create-full-row">
                    <label className="form-label">Manager</label>
                    <select
                      className="form-select"
                      value={newMember.manager_id}
                      onChange={(e) => setNewMember({ ...newMember, manager_id: e.target.value })}
                    >
                      <option value="">- Select -</option>
                      {managers.map((m) => (
                        <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group admin-create-full-row">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      value={newMember.address}
                      onChange={(e) => setNewMember({ ...newMember, address: e.target.value })}
                    />
                  </div>

                  <div className="form-group admin-create-full-row">
                    <label className="form-label">Temporary Address</label>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      value={newMember.temporary_address}
                      onChange={(e) => setNewMember({ ...newMember, temporary_address: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Emergency Contact Name</label>
                    <input
                      className="form-input"
                      value={newMember.emergency_contact_name}
                      onChange={(e) => setNewMember({ ...newMember, emergency_contact_name: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Emergency Contact Number</label>
                    <input
                      className="form-input"
                      value={newMember.emergency_contact_number}
                      onChange={(e) => setNewMember({ ...newMember, emergency_contact_number: e.target.value })}
                    />
                  </div>

                  <div className="form-group admin-create-full-row">
                    <label className="form-label">Emergency Contact Relation</label>
                    <input
                      className="form-input"
                      value={newMember.emergency_contact_relation}
                      onChange={(e) => setNewMember({ ...newMember, emergency_contact_relation: e.target.value })}
                    />
                  </div>

                  <label className="admin-create-checkbox">
                    <input
                      type="checkbox"
                      checked={newMember.is_active}
                      onChange={(e) => setNewMember({ ...newMember, is_active: e.target.checked })}
                    />
                    Active user
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)} disabled={creating}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
